"use client";
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiFetch } from '@/utils/api';

export type ThemeColor = 
  | 'violet' 
  | 'rose' 
  | 'emerald' 
  | 'indigo' 
  | 'pink' 
  | 'cyan' 
  | 'teal' 
  | 'lime' 
  | 'amber' 
  | 'orange' 
  | 'fuchsia' 
  | 'sky';

interface ThemeContextType {
  theme: ThemeColor;
  setTheme: (color: ThemeColor) => Promise<void>;
  loading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeColor>('violet');
  const [loading, setLoading] = useState(true);

  // Fetch theme from backend on mount
  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await apiFetch<{ success: boolean; data: { theme: ThemeColor } }>('/settings/theme');
        if (response?.data?.theme) {
          setThemeState(response.data.theme);
          applyTheme(response.data.theme);
        }
      } catch (error) {
        console.error('Failed to fetch theme:', error);
        // Fallback to default theme
        applyTheme('violet');
      } finally {
        setLoading(false);
      }
    };

    fetchTheme();
  }, []);

  const applyTheme = (color: ThemeColor) => {
    // Remove all theme classes from document
    const root = document.documentElement;
    const themeColors: ThemeColor[] = [
      'violet', 'rose', 'emerald', 'indigo', 'pink', 
      'cyan', 'teal', 'lime', 'amber', 'orange', 'fuchsia', 'sky'
    ];
    
    themeColors.forEach(c => {
      root.classList.remove(`theme-${c}`);
    });
    
    // Add new theme class
    root.classList.add(`theme-${color}`);
    
    // Set CSS variable for dynamic usage
    root.style.setProperty('--theme-color', color);
  };

  const setTheme = async (color: ThemeColor) => {
    try {
      // Save to backend
      await apiFetch('/settings/theme', {
        method: 'PUT',
        body: JSON.stringify({ theme: color }),
      });
      
      // Update local state
      setThemeState(color);
      applyTheme(color);
    } catch (error) {
      console.error('Failed to save theme:', error);
      throw error;
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};
